import React from 'react';

/**
 * Formata um valor numérico para formato de moeda (USD)
 * 🇧🇷 Mantém USD como moeda principal (frontend já mostra BRL abaixo)
 * @param {number} value - Valor a ser formatado
 * @param {boolean} showCents - Se deve mostrar os centavos (padrão: true)
 * @returns {string} Valor formatado como moeda
 */
export const formatCurrency = (value, showCents = true) => {
  if (value === undefined || value === null) return '$0.00';
  
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  });
  
  return formatter.format(value);
};

/**
 * Formata um valor numérico para formato de Bitcoin (BTC)
 * @param {number} value - Valor a ser formatado
 * @param {number} decimals - Número de casas decimais a mostrar (padrão: 8)
 * @returns {string} Valor formatado como Bitcoin
 */
export const formatBTC = (value, decimals = 8) => {
  if (value === undefined || value === null) return '0.00000000 BTC';
  
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  
  return `${formatter.format(value)} BTC`;
};

/**
 * Formata satoshis de forma inteligente (sats, k sats, M sats)
 * @param {number} btcAmount - Valor em BTC
 * @param {boolean} showBtc - Se deve mostrar equivalente em BTC (padrão: false)
 * @returns {string} Valor formatado em satoshis
 */
export const formatSatoshis = (btcAmount, showBtc = false) => {
  if (btcAmount === undefined || btcAmount === null) return '0 sats';
  
  const satoshis = Math.round(Math.abs(btcAmount) * 100000000);
  const btcFormatted = Math.abs(btcAmount).toFixed(8);
  
  let satoshiDisplay;
  
  if (satoshis < 1000) {
    // Menos de 1k sats: mostrar número completo
    satoshiDisplay = `${satoshis.toLocaleString('en-US')} sats`;
  } else if (satoshis < 1000000) {
    // 1k - 999k sats: mostrar em milhares
    const thousands = (satoshis / 1000).toFixed(1);
    satoshiDisplay = `${thousands.replace('.', ',')}k sats`;
  } else {
    // 1M+ sats: mostrar em milhões
    const millions = (satoshis / 1000000).toFixed(2);
    satoshiDisplay = `${millions.replace('.', ',')}M sats`;
  }
  
  if (showBtc) {
    return `${satoshiDisplay} (${btcFormatted} BTC)`;
  }
  
  return satoshiDisplay;
};

/**
 * Formata Bitcoin/Satoshis de forma inteligente para banners
 * Automaticamente escolhe a melhor representação visual
 * @param {number} btcAmount - Valor em BTC
 * @param {boolean} isCompact - Se deve usar formato compacto (padrão: false)
 * @returns {object} Objeto com display principal e secundário
 */
export const formatBitcoinSmart = (btcAmount, isCompact = false) => {
  if (btcAmount === undefined || btcAmount === null) {
    return {
      primary: '0 sats',
      secondary: '0.00000000 BTC',
      full: '0 sats (0.00000000 BTC)'
    };
  }
  
  const satoshis = Math.round(Math.abs(btcAmount) * 100000000);
  const btcFormatted = Math.abs(btcAmount).toFixed(8);
  const sign = btcAmount >= 0 ? '+' : '-';
  
  let primaryDisplay;
  let secondaryDisplay = `${btcFormatted} BTC`;
  
  if (satoshis < 1000) {
    // Menos de 1k sats: mostrar número completo
    primaryDisplay = `${sign}${satoshis.toLocaleString('en-US')} sats`;
  } else if (satoshis < 1000000) {
    // 1k - 999k sats: mostrar em milhares
    const thousands = (satoshis / 1000).toFixed(1);
    primaryDisplay = `${sign}${thousands.replace('.', ',')}k sats`;
  } else {
    // 1M+ sats: mostrar em milhões
    const millions = (satoshis / 1000000).toFixed(2);
    primaryDisplay = `${sign}${millions.replace('.', ',')}M sats`;
  }
  
  if (isCompact) {
    return {
      primary: primaryDisplay,
      secondary: secondaryDisplay,
      full: `${primaryDisplay} (${secondaryDisplay})`
    };
  }
  
  return {
    primary: primaryDisplay,
    secondary: `(${secondaryDisplay})`,
    full: `${primaryDisplay} (${secondaryDisplay})`
  };
};

/**
 * Formata um valor percentual
 * @param {number} value - Valor a ser formatado
 * @param {number} decimals - Número de casas decimais a mostrar (padrão: 2)
 * @returns {string} Valor formatado como percentual
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value === undefined || value === null) return '0.00%';
  
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  
  return formatter.format(value / 100);
};

/**
 * Formata uma data para exibição
 * 🇧🇷 Adaptado para o público brasileiro - formato DD/MM/YYYY
 * @param {string|Date} date - Data a ser formatada
 * @param {boolean} showTime - Se deve mostrar o horário (padrão: false)
 * @returns {string} Data formatada
 */
export const formatDate = (date, showTime = false) => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  
  if (showTime) {
    // 🇧🇷 Formato brasileiro: DD/MM/YYYY HH:mm
    return dateObj.toLocaleString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  
  // 🇧🇷 Formato brasileiro: DD/MM/YYYY
  return dateObj.toLocaleDateString('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

/**
 * Retorna o nome capitalizado do regime
 * @param {string} regime - Nome do regime
 * @returns {string} Nome capitalizado
 */
export const formatRegimeName = (regime) => {
  if (!regime) return '';
  
  return regime
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}; 

/**
 * Processa texto markdown simples para React
 * Converte **texto** em <strong>texto</strong>
 * @param {string} text - Texto com formatação markdown
 * @returns {JSX.Element} - Elemento React com formatação aplicada
 */
export const parseMarkdownText = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Split the text by **bold** patterns
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      // Remove ** and wrap in <strong>
      const boldText = part.slice(2, -2);
      return <strong key={index}>{boldText}</strong>;
    }
    return part;
  });
}; 